import React from 'react'
import { Well } from '@pushly/aqe/lib/components'
import { Form } from '@ant-design/compatible'
import '@ant-design/compatible/assets/index.css'
import { Input, Tooltip } from 'antd'
import { SimpleTimeframe } from '../../../../components/simple-timeframe/simple-timeframe'
import { InfoCircleOutlined } from '@ant-design/icons'
import {
    CAMPAIGN_ABANDONED_BROWSE_DEFAULT_ITEM_VIEWS,
    CAMPAIGN_ABANDONED_BROWSE_MAXIMUM_ITEM_VIEWS,
    CAMPAIGN_ABANDONED_BROWSE_MINIMUM_ITEM_VIEWS,
    CAMPAIGN_DEFAULT_ABANDONED_CART_COOLDOWN_PERIOD,
} from '../../../../components/campaign-builder/configuration-editor/constants'
import './abandonment-settings-section.scss'
import { RelativeDateDisplayMetric } from '@pushly/aqe/lib/enums'
import { CatalogItemType } from '@pushly/models/lib/enums'
import {
    getPluralForCatalogItemType,
    getSingularForCatalogItemType,
} from '@pushly/models/lib/enums/catalogs/catalog-item-type'
import { CampaignConfigurationModel } from '../../../../models/campaign/campaign-configuration.model'
import { titleCase } from '../../../../_utils/utils'
import { TriggerType } from '@pushly/models/lib/enums/journeys'

export interface IAbandonmentSettingsSectionProps {
    showAbandonment: boolean
    showCooldown?: boolean
    showMinimumItemViews?: boolean
    showMaxItemAge: boolean

    itemType: CatalogItemType
    triggerType: TriggerType

    // Models
    configuration: CampaignConfigurationModel
    triggerConfig: unknown // object exposing getConfiguration()/setConfiguration() or params
    defaultMaxItemAgeDays?: number

    // Single mutator apply pattern: caller clones campaign and applies
    onApply: (apply: (cfg: CampaignConfigurationModel, trig: unknown) => void) => void
}

const TitleWithInfo: React.FC<{ title: string; tooltip: string }> = ({ title, tooltip }) => (
    <div className="well-title-with-info" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <span>{title}</span>
        <Tooltip title={tooltip}>
            <InfoCircleOutlined className="info-icon" />
        </Tooltip>
    </div>
)

const AbandonmentSettingsSectionCmp: React.FC<IAbandonmentSettingsSectionProps> = ({
    showAbandonment,
    showCooldown,
    showMinimumItemViews,
    showMaxItemAge,
    itemType,
    triggerType,
    configuration,
    triggerConfig,
    onApply,
    defaultMaxItemAgeDays,
}) => {
    const itemTypePlural = getPluralForCatalogItemType(itemType)
    const itemTypeSingular = getSingularForCatalogItemType(itemType)

    let maxItemAgeActionString = ""
    switch (triggerType) {
        case TriggerType.ABANDONED_CART:
            maxItemAgeActionString = "carted"
            break
        case TriggerType.ABANDONED_VIEW:
            maxItemAgeActionString = "viewed"
            break
        case TriggerType.SAVED_ITEM:
            maxItemAgeActionString = "saved"
            break;
    }

    return (
        <>
            {showAbandonment && (
                <Well
                    mode="ghost"
                    title={
                        <TitleWithInfo
                            title="Abandonment timeframe"
                            tooltip={`A subscriber will qualify for this campaign after they have carted at least one ${itemTypeSingular} and not been active for the specified amount of time.`}
                        />
                    }
                    hideFooter
                    className="entry-cap-well"
                >
                    <div className="rcte-body-compact">
                        <Form.Item className="campaign-trigger-editor-label" colon={false} style={{ margin: 0 }}>
                            <div className="rcte-inline-sentence">
                                <span>Consider the subscriber abandoned after</span>
                                <span className="rcte-control">
                                    <SimpleTimeframe
                                        size="small"
                                        disabledMetrics={['seconds']}
                                        value={{
                                            seconds: configuration?.getAbandonmentSeconds?.() ?? 3600,
                                            metric: (configuration?.getAbandonmentMetric?.() as any) ?? 'minutes',
                                        }}
                                        onChange={(v: any) =>
                                            onApply((cfg) => {
                                                cfg?.setAbandonmentSeconds?.(v?.seconds)
                                                cfg?.setAbandonmentMetric?.(v?.metric)
                                            })
                                        }
                                    />
                                </span>
                                <span>of inactivity.</span>
                            </div>
                        </Form.Item>
                    </div>
                </Well>
            )}

            {showCooldown && (
                <Well
                    mode="ghost"
                    title={
                        <TitleWithInfo
                            title="Browse abandonment eligibility"
                            tooltip="Prevents a subscriber from entering a browse abandonment campaign immediately after participating in an cart abandonment campaign."
                        />
                    }
                    hideFooter
                    className="entry-cap-well"
                >
                    <div className="rcte-body-compact">
                        <Form.Item className="campaign-trigger-editor-label" colon={false} style={{ margin: 0 }}>
                            <div className="rcte-inline-sentence">
                                <span>After completing an cart abandonment campaign, wait at least</span>
                                <span className="rcte-control">
                                    <SimpleTimeframe
                                        size="small"
                                        disabledMetrics={['seconds', 'minutes', 'hours']}
                                        value={{
                                            seconds:
                                                configuration?.getAbandonedCartCooldownPeriod?.()?.interval_seconds ??
                                                CAMPAIGN_DEFAULT_ABANDONED_CART_COOLDOWN_PERIOD.interval_seconds,
                                            metric: RelativeDateDisplayMetric.DAYS as any,
                                        }}
                                        onChange={(v: any) =>
                                            onApply((cfg) => cfg?.setAbandonedCartCooldownPeriod?.(v?.seconds))
                                        }
                                    />
                                </span>
                                <span>before allowing entry into this browse abandonment campaign.</span>
                            </div>
                        </Form.Item>
                    </div>
                </Well>
            )}

            {showMinimumItemViews && (
                <Well
                    mode="ghost"
                    title={
                        <TitleWithInfo
                            title={`Minimum ${itemTypePlural} views to qualify`}
                            tooltip={`The subscriber must view the same ${itemTypeSingular} at least the specified number times to qualify for the campaign.`}
                        />
                    }
                    hideFooter
                    className="entry-cap-well"
                >
                    <div className="rcte-body-compact">
                        <Form.Item className="campaign-trigger-editor-label" colon={false} style={{ margin: 0 }}>
                            <div className="rcte-inline-sentence">
                                <span>Require a {itemTypeSingular} to be viewed at least</span>
                                <span className="rcte-control">
                                    <Input
                                        aria-label={`Required ${itemTypePlural} view count`}
                                        type="number"
                                        size="small"
                                        min={CAMPAIGN_ABANDONED_BROWSE_MINIMUM_ITEM_VIEWS}
                                        max={CAMPAIGN_ABANDONED_BROWSE_MAXIMUM_ITEM_VIEWS}
                                        placeholder={`${CAMPAIGN_ABANDONED_BROWSE_MINIMUM_ITEM_VIEWS}–${CAMPAIGN_ABANDONED_BROWSE_MAXIMUM_ITEM_VIEWS}`}
                                        defaultValue={CAMPAIGN_ABANDONED_BROWSE_DEFAULT_ITEM_VIEWS}
                                        value={configuration?.getAbandonedBrowseMinimumItemViews?.()}
                                        onChange={(e) =>
                                            onApply((cfg) => {
                                                const parsed = Number.parseInt(e?.target?.value, 10)
                                                cfg?.setAbandonedBrowseMinimumItemViews?.(
                                                    Number.isNaN(parsed) ? undefined : parsed,
                                                )
                                            })
                                        }
                                    />
                                </span>
                                <span>time(s) to be eligible for this campaign.</span>
                            </div>
                        </Form.Item>
                    </div>
                </Well>
            )}

            {showMaxItemAge && (
                <Well
                    mode="ghost"
                    title={
                        <TitleWithInfo
                            title={`Maximum ${maxItemAgeActionString} ${itemTypePlural} age`}
                            tooltip={`${titleCase(maxItemAgeActionString)} ${itemTypePlural} older than the specified number of days are excluded from this campaign.`}
                        />
                    }
                    hideFooter
                    className="entry-cap-well"
                >
                    <div className="rcte-body-compact">
                        <Form.Item className="campaign-trigger-editor-label" colon={false} style={{ margin: 0 }}>
                            <div className="rcte-inline-sentence">
                                <span>Exclude {itemTypePlural} that were {maxItemAgeActionString} more than </span>
                                <span className="rcte-control">
                                    <SimpleTimeframe
                                        size="small"
                                        disabledMetrics={['seconds', 'minutes', 'hours']}
                                        value={{
                                            seconds: (() => {
                                                const trigCfg = (triggerConfig as any)?.getConfiguration?.()
                                                const raw = (trigCfg?.params?.max_item_age_days ??
                                                    (triggerConfig as any)?.params?.max_item_age_days) as
                                                    | number
                                                    | undefined
                                                const days = raw ?? defaultMaxItemAgeDays ?? 0
                                                return days * 24 * 60 * 60
                                            })(),
                                            metric: RelativeDateDisplayMetric.DAYS as any,
                                        }}
                                        onChange={(v: any) =>
                                            onApply((cfg, trig) => {
                                                const t = trig as any
                                                const currentCfg = (t?.getConfiguration?.() ?? t ?? {}) as any
                                                const currentParams = currentCfg.params ?? {}
                                                const days = (v?.seconds ?? 0) / (24 * 60 * 60)
                                                const nextCfg = {
                                                    ...currentCfg,
                                                    params: {
                                                        ...currentParams,
                                                        max_item_age_days: Number.isFinite(days) ? Math.floor(days) : 0,
                                                    },
                                                }
                                                if (t?.setConfiguration) {
                                                    t.setConfiguration(nextCfg)
                                                } else if (t) {
                                                    t.params = nextCfg.params
                                                }
                                            })
                                        }
                                    />
                                </span>
                                <span> ago.</span>
                            </div>
                        </Form.Item>
                    </div>
                </Well>
            )}
        </>
    )
}

export default React.memo(AbandonmentSettingsSectionCmp)
